import React, { useState, useEffect, useRef } from 'react';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import MonitorCard from 'components/Dashboard/MonitorCard';
import StatCards from 'components/Dashboard/StatCards';
import SectionIntro from 'components/common/SectionIntro';
import TableComponent from 'components/common/TableComponent';
import { useTableData } from 'hooks/useTableData';
import { makeRequest } from 'services/request.service';
import { useAuth } from 'auth/AuthContext';

// GET request for packing summary data
const getPackingSummaryData = async (farmId, filterDate) => {
  try {
    const response = await makeRequest({
      method: 'GET',
      url: `/dashboard/${farmId}/packing-summary?filterDate=${new Date(filterDate).toISOString()}`,
      headers: {
        Authorization: `Bearer ${localStorage.getItem('secret')}`
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching packing summary data:', error);
    throw error;
  }
};

const themeColorMap = shade => {
  return {
    700: 'workspace-stat-card--accent',
    600: 'workspace-stat-card--accent',
    500: 'workspace-stat-card--accent-soft',
    400: 'workspace-stat-card--accent-soft'
  }[shade];
};

const DailyPackingSummary = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const hasFetched = useRef(false);

  // Get current date in Mexico City timezone for date picker
  const currentDateForPicker = DateTime.now().setZone('America/Mexico_City').toFormat('yyyy-MM-dd');

  // New state for selected date
  const [selectedDate, setSelectedDate] = useState(currentDateForPicker);

  // State for API data
  const [packingSummaryData, setPackingSummaryData] = useState({
    dailyPackingSummary: {
      totalPacked: 0,
      packerCount: 0,
      averagePerWorker: 0,
      averagePackedPerHour: 0
    },
    dailyPackingSummarySameHourPreviousDay: {
      totalPacked: 0,
      packerCount: 0
    },
    packerGroupsMonitor: [],
    packersMonitor: [],
    loading: false,
    error: null
  });

  const fetchPackingSummaryData = async (dateToFetch = selectedDate) => {
    if (!user?.farmId) return;

    try {
      setPackingSummaryData(prev => ({ ...prev, loading: true, error: null }));

      const data = await getPackingSummaryData(user.farmId, dateToFetch);

      setPackingSummaryData(prev => ({
        ...prev,
        dailyPackingSummary: data.dailyPackingSummary || prev.dailyPackingSummary,
        dailyPackingSummarySameHourPreviousDay:
          data.dailyPackingSummarySameHourPreviousDay ||
          prev.dailyPackingSummarySameHourPreviousDay,
        packerGroupsMonitor: data.packerGroupsMonitor || [],
        packersMonitor: data.packersMonitor || [],
        loading: false
      }));
    } catch (error) {
      console.error('Error fetching packing summary:', error);
      setPackingSummaryData(prev => ({
        ...prev,
        loading: false,
        error: 'Failed to load packing summary data'
      }));
    }
  };

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;

    fetchPackingSummaryData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.farmId]);

  // Refetch when selectedDate changes
  useEffect(() => {
    if (!hasFetched.current) return;
    fetchPackingSummaryData(selectedDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  // Create cards data from API response
  const createCardsData = () => {
    const { dailyPackingSummary, dailyPackingSummarySameHourPreviousDay } = packingSummaryData;
    const selectedDateFormatted = DateTime.fromISO(selectedDate).toFormat('dd.MM.yyyy');

    return [
      {
        title: t('dashboard.totalPacked'),
        value: Number(dailyPackingSummary.totalPacked || 0).toLocaleString(),
        unit: t('dashboard.units'),
        sub: `${t('dashboard.lastPackingDay')} (${selectedDateFormatted})`,
        color: 'workspace-stat-card--accent'
      },
      {
        title: t('dashboard.totalPacked'),
        value: Number(dailyPackingSummarySameHourPreviousDay.totalPacked || 0).toLocaleString(),
        unit: t('dashboard.units'),
        sub: t('dashboard.sameHourPrevious'),
        color: themeColorMap(500)
      },
      {
        title: t('dashboard.packersCount'),
        value: Number(dailyPackingSummary.packerCount || 0).toString(),
        unit: t('dashboard.workers'),
        sub: `${t('dashboard.lastPackingDay')} (${selectedDateFormatted})`,
        color: 'workspace-stat-card--accent'
      },
      {
        title: t('dashboard.packersCount'),
        value: Number(dailyPackingSummarySameHourPreviousDay.packerCount || 0).toString(),
        unit: t('dashboard.workers'),
        sub: t('dashboard.sameHourPrevious'),
        color: themeColorMap(500)
      },
      {
        title: t('dashboard.avgPerPacker'),
        value: Number(dailyPackingSummary.averagePerWorker || 0).toFixed(2),
        unit: t('dashboard.units'),
        sub: `${t('dashboard.lastPackingDay')} (${selectedDateFormatted})`,
        color: 'workspace-stat-card--accent'
      },
      {
        title: t('dashboard.workerAvg'),
        value: Number(dailyPackingSummary.averagePackedPerHour || 0).toFixed(2),
        unit: t('dashboard.unitsPerHour'),
        sub: `${t('dashboard.lastPackingDay')} (${selectedDateFormatted})`,
        color: 'workspace-stat-card--accent'
      }
    ];
  };

  // Use the custom hook to generate table data with totals
  const packerGroupsTableData = useTableData(
    packingSummaryData.packerGroupsMonitor,
    'packerGroups'
  );
  const packerMonitorTableData = useTableData(packingSummaryData.packersMonitor, 'packerMonitor');

  if (packingSummaryData.loading) {
    return (
      <div className="min-h-screen p-6">
        <div className="flex justify-center items-center h-64">
          <div className="workspace-summary-message text-lg">
            {t('dashboard.loadingPackingSummary')}
          </div>
        </div>
      </div>
    );
  }

  if (packingSummaryData.error) {
    return (
      <div className="min-h-screen p-6">
        <div className="flex justify-center items-center h-64">
          <div className="workspace-summary-error text-lg">{packingSummaryData.error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6">
      <SectionIntro
        kicker={t('pageIntro.dashboardKicker')}
        title={t('dashboard.dailyPackingSummary')}
        description={t('pageIntro.dailyPackingSummaryDescription')}
        actions={
          <div className="flex items-center gap-2">
            <label htmlFor="pack-date" className="workspace-summary-date-label text-sm">
              {t('dashboard.selectDate')}
            </label>
            <input
              id="pack-date"
              type="date"
              className="workspace-summary-date-input rounded border px-2 py-1 text-sm"
              value={selectedDate}
              max={currentDateForPicker}
              onChange={e => setSelectedDate(e.target.value)}
            />
          </div>
        }
        className="mb-4"
      />

      <hr className="workspace-summary-divider mb-6 border-t" />

      <StatCards cards={createCardsData()} />

      <div className="grid gap-4 mt-4">
        <MonitorCard
          title={t('dashboard.packerGroupsMonitor')}
          date={DateTime.fromISO(selectedDate).toFormat('dd.MM.yyyy')}
          hasData={packerGroupsTableData.rows.length > 0}
          color="green"
        >
          <TableComponent
            tableData={packerGroupsTableData}
            defaultSortColumn={packerGroupsTableData.columns[0]?.key}
          />
        </MonitorCard>

        <MonitorCard
          title={t('dashboard.packerMonitor')}
          date={DateTime.fromISO(selectedDate).toFormat('dd.MM.yyyy')}
          hasData={packerMonitorTableData.rows.length > 0}
          color="green"
        >
          <TableComponent
            tableData={packerMonitorTableData}
            defaultSortColumn={packerMonitorTableData.columns[0]?.key}
          />
        </MonitorCard>
      </div>
    </div>
  );
};

export default DailyPackingSummary;
